/* eslint-disable object-shorthand */
import lGet from 'lodash.get';
import classPropper, { Validator } from 'class-propper';
import { Component } from 'react';
import { EFFECTS_NOOP } from '../constants';
// import { actionsValidator } from '../../utils/validators';
import blend from '../blend';

/**
 * freactalStore is a Store model that reflects parented state/actions.
 * It exists in one of two modalities:
 *
 * 1) it is paired with a component, in which case it uses that components' state
 *    as the state collection
 *
 * 2) it is free-standing, in which case it has an independent state collection.
 *
 * in any case it inherits the state and actions of its parent.
 *
 * Note by default the inheritance is replacement. However,
 * custom reducers for actions and state can be injected to allow for a
 * customized blending of the parents' state and this state.
 * (you could for instance combine array values, prefer parents' values over
 * this ones, or only accept some of the parents state or actions into the current state.)
 *
 * Pro tip, you can use things like reselect to create computed store values.
 *
 * there is a recurring pattern at work here for state and actions;
 *
 * ___ is the local read-only state/action item. "____"
 * combined____ is the union of the my___ and any parents' ___
 * either through desctructuring (the default) or with a reduce____ method injection.
 * in a refreshCombined___ method.
 *
 *
 */

export { EFFECTS_NOOP };
const noop = () => {};

class FreactalStore {
  constructor(config) {
    const actions = config.actions || {};
    delete config.actions;
    this._baseActions = actions;
    Object.assign(this, config);
    this.refreshActions();
  }

  get combinedActions() {
    if (!this._combinedActions) this.refreshCombinedActions();
    return this._combinedActions;
  }

  get actions() {
    if (!this._actions) {
      this.refreshActions();
    }
    return this._actions;
  }

  refreshActions() {
    const actions = this.parent ? this.parent.combinedActions : {};

    Object.keys(this._baseActions).forEach((key) => {
      actions[key] = (...args) => new Promise(async (resolve, fail) => {
        let value;
        try {
          value = await this._baseActions[key]({
            actions: this.combinedActions,
            state: this.combinedState,
            store: this,
          }, ...args);
        } catch (err) {
          return fail(err);
        }
        return (value === EFFECTS_NOOP) ? resolve() :
          this.setState(value, resolve);
      });
    });

    this._actions = actions;
    this.refreshCombinedActions();
  }

  refreshCombinedActions() {
    const parentActions = lGet(this, 'parent.combinedActions', false);
    const reducer = lGet(this, 'actionsReducer') || blend;
    this._combinedActions = reducer(this.actions, parentActions);
  }

  get state() {
    if (this.component) {
      return this.component.state;
    } else if (!this._state) {
      this._state = lGet(this, 'initializer', () => ({}))();
    }

    return this._state;
  }

  setState(update, done = noop) {
    const finish = () => {
      this.refreshCombinedState();
      if (this.component && this.component.forceUpdate) {
        this.component.forceUpdate();
      }
      done();
    };
    if (this.component) {
      this.component.setState(update, finish);
    } else {
      this._state = { ...this.state, ...update };
      finish();
    }
  }

  get combinedState() {
    if (!this._combinedState) {
      this.refreshCombinedState();
    }
    return this._combinedState;
  }

  /**
   * returns a union of this component's state and the parent's combinedState.
   * Note for object identity, if there is no parent, should be == this.component.state
   */
  refreshCombinedState() {
    const parentState = lGet(this, 'parent.combinedState', false);
    const reducer = lGet(this, 'stateReducer') || blend;
    this._combinedState = reducer(this.state, parentState);
  }
}

const cp = classPropper(FreactalStore);

const parentValidator = new Validator((parent) => {
  if (!parent) {
    return false;
  }
  if (!(parent instanceof FreactalStore)) {
    return 'parent must be a freactalStore';
  }
  return false;
});

cp.addProp('initializer', {
  defaultValue: () => ({}),
  type: 'function',
})
  .addProp('actions', {
    onSet: function () { this.refreshActions(); },
  })
  .addProp('component', { failsWhen: comp => !(comp instanceof Component) })
  .addProp('stateReducer', { type: 'function' })
  .addProp('actionsReducer', { type: 'function' })
  .addProp('parent', {
    failsWhen: parentValidator,
    onSet: function () { this.refreshCombinedActions(); this.refreshCombinedState(); },
  });

export default FreactalStore;



// WEBPACK FOOTER //
// ./src/components/FreactalStore/FreactalStore.js